import type { ReactElement } from "react"
import { useState } from "react"
import FullWidthSliderMol from "@onestore/hel/dist/components/molecules/FullWidthSliderMol"
import _t from "@core/i18n"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import SliderSlide from "@gatsby-plugin-generic-page/components/SliderSlide"
import type { FullWidthSliderSectionFlatData } from "@gatsby-plugin-generic-page/fragments/fullWidthSliderSection"
import { arrowLeft, arrowRight } from "@ionos-web-design-system/icon/system"
import Button, { type ButtonProps } from "@ionos-web-design-system/react/button"
import SliderIndicator from "@ionos-web-design-system/react/slider-indicator"

type SliderArrowProps = ButtonProps & {
  // Injected by react-slick's cloneElement on prevArrow/nextArrow. They must be
  // destructured out here so they aren't spread onto the underlying DOM button,
  // which otherwise triggers "React does not recognize the `currentSlide`/
  // `slideCount` prop on a DOM element" console warnings.
  currentSlide?: number
  slideCount?: number
}

function SliderArrow({
  currentSlide: _currentSlide,
  slideCount: _slideCount,
  ...buttonProps
}: SliderArrowProps): ReactElement<ButtonProps> {
  return <Button {...buttonProps} />
}

export default function FullWidthSliderSection({
  sections,
  sectionSettings,
}: FullWidthSliderSectionFlatData): ReactElement<FullWidthSliderSectionFlatData> {
  const [activeSlide, setActiveSlide] = useState(0)

  const sliderIndicators = (
    <>
      <div className="flex justify-between items-center w-full px-3 sm:hidden">
        <SliderIndicator
          count={sections.length}
          activeIndex={activeSlide}
          onDotClick={setActiveSlide}
          getAriaLabel={(index) => `${_t("slider.dot")} ${index + 1}`}
          variant="default"
          infinite
        />
      </div>

      <div className="hidden sm:block">
        <SliderIndicator
          count={sections.length}
          activeIndex={activeSlide}
          onDotClick={setActiveSlide}
          getAriaLabel={(index) => `${_t("slider.dot")} ${index + 1}`}
          variant="light"
        />
      </div>
    </>
  )

  return (
    <BaseSection {...sectionSettings} hasOverflowHidden>
      <BaseContainer {...sectionSettings?.containerSettings}>
        <div className="[&_.slick-track]:!flex [&_.slick-slide]:!h-auto [&_.slick-slide>div]:h-full [&_.focusable-slide-wrapper]:h-full">
          <FullWidthSliderMol
            slides={sections.map((sectionData, index) => ({
              key: `slide-${index}`,
              slide: (
                <SliderSlide {...sectionData} isFirstSlide={index === 0} />
              ),
            }))}
            activeSlide={activeSlide}
            onSlideChange={setActiveSlide}
            sliderIndicators={sliderIndicators}
            slideTitle={{
              next: _t("slider.next"),
              previous: _t("slider.previous"),
            }}
            // onClick intentionally omitted: react-slick injects its own via cloneElement
            prevArrow={
              <SliderArrow
                icon={arrowLeft}
                concept="monochrome"
                variant="secondary"
                size="medium"
                tabIndex={-1}
                aria-label={_t("slider.previous")}
              />
            }
            nextArrow={
              <SliderArrow
                icon={arrowRight}
                concept="monochrome"
                variant="secondary"
                size="medium"
                tabIndex={-1}
                aria-label={_t("slider.next")}
              />
            }
          />
        </div>
      </BaseContainer>
    </BaseSection>
  )
}
